import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  Cell,
} from "recharts";
import { Modal, Button, Spin, message, Typography } from "antd";
import Sleep from "../../../Assets/Images/Svg/Sleep.svg";
import { IoAlarmOutline } from "react-icons/io5";
import { LuBedDouble } from "react-icons/lu";
import { PiTimer } from "react-icons/pi";

const SleepData = ({ smartWatchId }) => {
  const [sleep, setSleep] = useState([]);
  const [sleepDetails, setSleepDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (smartWatchId) fetchSleepData();
  }, [smartWatchId]);

  const fetchSleepData = async (fromDate, toDate) => {
    const today = dayjs();
    const start = fromDate
      ? dayjs(fromDate).format("DD/MM/YYYY")
      : today.format("DD/MM/YYYY");
    const end = toDate ? dayjs(toDate).format("DD/MM/YYYY") : start;

    try {
      setLoading(true);
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 7,
        offset: 0,
        fromDate: start,
        toDate: end,
        type: "SLEEPS",
      });

      if (res?.data.length > 0) {
        const parsedData = res.data.map((item) => {
          let raw = {};
          try {
            raw = JSON.parse(item.RAW_JSON || "{}");
          } catch {}
          return { ...item, RAW_JSON: raw };
        });
        setSleep(parsedData);
        setSleepDetails(parsedData[0]);
      } else {
        setSleep([]);
        setSleepDetails({});
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return "0 hr 0 min";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hrs} hr${hrs !== 1 ? "s" : ""} ${mins} min${
      mins !== 1 ? "s" : ""
    }`;
  };

  const showGraph = () => {
    if (!smartWatchId) return;
    setIsModalOpen(true);
  };
  const handleClose = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
  };

  return (
    <div>
      <div
        title="Click to view Graph"
        className="bg-white shadow rounded-lg p-4 w-full h-40 cursor-pointer"
        onClick={showGraph}
      >
        <div className="flex items-center gap-2 self-start mb-4">
          <img src={Sleep} alt="icon" />
          <h2 className="text-sm font-sans font-semibold">Sleep</h2>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-full w-full">
            <div className="w-3/4 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-horizontal-pulse"></div>
            </div>
            <p className="mt-3 text-gray-600 text-sm font-medium">Loading...</p>
          </div>
        ) : (
          <div className="mt-4 text-[11px] grid grid-cols-2 gap-y-3">
            <div>
              <p className="flex items-center gap-1 font-sans font-semibold text-[11px]">
                <IoAlarmOutline className="text-[12px]" />
                {formatTime(sleepDetails?.DURATION_SEC)}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5">Duration</p>
            </div>

            <div>
              <p className="flex items-center gap-1 font-sans font-semibold text-[11px]">
                <LuBedDouble className="text-[12px]" />
                {formatTime(sleepDetails?.DEEP_SEC)}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5">Deep Sleep</p>
            </div>

            <div>
              <p className="flex items-center gap-1 font-sans font-semibold text-[11px]">
                <PiTimer className="text-[12px]" />
                {formatTime(sleepDetails?.LIGHT_SEC)}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5">Light Sleep</p>
            </div>
          </div>
        )}
      </div>

      {smartWatchId && (
        <SleepModal
          open={isModalOpen}
          onClose={handleClose}
          smartWatchId={smartWatchId}
          sleepDetails={sleepDetails}
        />
      )}
    </div>
  );
};

const SleepModal = ({ open, onClose, smartWatchId, sleepDetails }) => {
  const [tab, setTab] = useState("1D");
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({
    total: "0 hr 0 min",
    deep: "0 hr 0 min",
    light: "0 hr 0 min",
    rem: "0 hr 0 min",
    awake: "0 hr 0 min",
  });
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const STAGE_CONFIG = {
    deep: { level: 1, label: "Deep", color: "#1e3a8a" },
    light: { level: 2, label: "Light", color: "#2563eb" },
    rem: { level: 3, label: "REM", color: "#8b5cf6" },
    awake: { level: 4, label: "Awake", color: "#f43f5e" },
  };

  const CustomLegend = () => {
    return (
      <div className="flex gap-4 flex-wrap justify-center">
        {Object.values(STAGE_CONFIG).map((stage) => (
          <div
            key={stage.level}
            className="flex justify-center items-center gap-1"
          >
            <div
              className="w-4 h-4 rounded-md"
              style={{
                backgroundColor: stage.color,
              }}
            ></div>
            <span>{stage.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const fetch1DSleepData = async () => {
    if (!smartWatchId) return;

    try {
      const date = selectedDate.format("DD/MM/YYYY");
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 7,
        offset: 0,
        fromDate: date,
        toDate: date,
        type: "SLEEPS",
      });

      if (res?.data?.length > 0) {
        const item = res.data[0];
        let raw = {};
        try {
          raw = JSON.parse(item.RAW_JSON || "{}");
        } catch {}
        const sleepLevelsMap = raw.sleepLevelsMap;
        const buildSleepChartData = (sleepLevelsMap) => {
          const points = [];
          Object.entries(sleepLevelsMap).forEach(([stageKey, ranges]) => {
            const cfg = STAGE_CONFIG[stageKey];
            if (!cfg) return;
            ranges.forEach(({ startTimeInSeconds, endTimeInSeconds }) => {
              const STEP = 120;
              for (
                let t = startTimeInSeconds;
                t < endTimeInSeconds;
                t += STEP
              ) {
                points.push({
                  time: dayjs.unix(t).format("h:mm a"),
                  stage: cfg.level,
                  color: cfg.color,
                  ts: t,
                });
              }
            });
          });
          return points.sort((a, b) => a.ts - b.ts);
        };

        const graphData = buildSleepChartData(sleepLevelsMap);
        setChartData(graphData);

        const sleepSummary = item.sleepSummary
          ? JSON.parse(item.sleepSummary)
          : {};
        setSummary({
          total: sleepSummary?.TotalSleep || "0 hr 0 min",
          deep: sleepSummary?.DeepSleep || "0 hr 0 min",
          light: sleepSummary?.LightSleep || "0 hr 0 min",
          rem: sleepSummary?.REMSleep || "0 hr 0 min",
          awake: sleepSummary?.Awake || "0 hr 0 min",
        });
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const stageLabels = {
    1: "Deep",
    2: "Light",
    3: "REM",
    4: "Awake",
  };

  const fetch7DSleepData = async () => {
    if (!smartWatchId) return;
    try {
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 100,
        offset: 0,
        fromDate: startDate.format("DD/MM/YYYY"),
        toDate: endDate.format("DD/MM/YYYY"),
        type: "SLEEPS",
      });

      if (res?.data?.length > 0) {
        const parsedData = res.data
          .map((item) => {
            let raw = {};
            try {
              raw = JSON.parse(item.RAW_JSON || "{}");
            } catch {}
            const sleepDate = item.SLEEP_DATE
              ? dayjs(item.SLEEP_DATE)
              : dayjs(item.CREATED_AT || item.UPDATED_AT || new Date());
            return { ...item, parsedDate: sleepDate, RAW_JSON: raw };
          })
          .sort((a, b) => a.parsedDate.valueOf() - b.parsedDate.valueOf());

        const lineData = parsedData.map((d) => ({
          date: d.parsedDate.format("DD MMM"),
          avg: d.RAW_JSON?.overallSleepScore?.value || 0,
        }));

        setChartData(lineData);
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error(err);
      setChartData([]);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DSleepData();
    } else {
      fetch7DSleepData();
    }
  }, [tab, smartWatchId, selectedDate, startDate, endDate]);

  const handlePrev1Day = () =>
    setSelectedDate((prev) => prev.subtract(1, "day"));
  const handleNext1Day = () => {
    setSelectedDate((prev) => {
      const next = prev.add(1, "day");
      if (next.isAfter(dayjs(), "day")) {
        message.info("Data for future dates will be available tomorrow.");
        return prev;
      }
      return next;
    });
  };

  const handlePrev7D = () => {
    setStartDate((prev) => prev.subtract(7, "day"));
    setEndDate((prev) => prev.subtract(7, "day"));
  };
  const handleNext7D = () => {
    const tomorrow = dayjs().add(1, "day").startOf("day");

    const proposedEnd = endDate.add(7, "day");

    if (proposedEnd.isAfter(tomorrow, "day")) {
      message.info("Data for future dates will be available tomorrow.");
      return;
    }

    const newStart = startDate.add(7, "day");
    const newEnd = endDate.add(7, "day");

    setStartDate(newStart);
    setEndDate(newEnd);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      title="Sleep Graph"
    >
      <div className="flex flex-col items-center">
        <div className="flex justify-center mb-4 gap-3">
          <button
            onClick={() => setTab("1D")}
            className={`${
              tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
            } px-4 py-2 rounded-md`}
          >
            1D
          </button>
          <button
            className={`${
              tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
            } px-4 py-2 rounded-md`}
            onClick={() => setTab("7D")}
          >
            7D
          </button>
        </div>

        {tab === "7D" && (
          <div className="flex justify-between w-full mb-4">
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handlePrev7D}
            >
              ← Prev 7 Days
            </button>
            <span>
              {startDate.format("DD/MM/YYYY")} - {endDate.format("DD/MM/YYYY")}
            </span>
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handleNext7D}
            >
              Next 7 Days →
            </button>
          </div>
        )}

        {tab === "1D" ? (
          <>
            <div className="flex items-center justify-center gap-6 mb-4">
              <button
                className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
                onClick={handlePrev1Day}
              >
                ←
              </button>
              <strong>{selectedDate.format("DD MMM YYYY")}</strong>
              <button
                className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
                onClick={handleNext1Day}
              >
                →
              </button>
            </div>

            {chartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} barCategoryGap={0}>
                    <XAxis
                      dataKey="time"
                      interval={20} // 👈 show 1 label for every 20 bars
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis
                      type="number"
                      domain={[0, 4]}
                      ticks={[1, 2, 3, 4]}
                      tickFormatter={(v) =>
                        ({ 1: "Deep", 2: "Light", 3: "REM", 4: "Awake" })[v]
                      }
                    />
                    <Tooltip
                      formatter={(value) => stageLabels[value]}
                      labelFormatter={(label) => `Time: ${label}`}
                    />
                    <Legend content={<CustomLegend />} />
                    <Bar dataKey="stage">
                      {chartData.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                <div className="mt-4 text-center text-sm">
                  <span className="font-semibold">Total: {summary.total}</span>
                  <span className="ml-3 font-semibold">
                    Deep: {summary.deep}
                  </span>
                  <span className="ml-3 font-semibold">
                    Light: {summary.light}
                  </span>
                  <span className="ml-3 font-semibold">REM: {summary.rem}</span>
                  <span className="ml-3 font-semibold">
                    Awake: {summary.awake}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-gray-500 mt-3 h-72">No sleep data available</p>
            )}
          </>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="avg"
                stroke="#f97316"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </Modal>
  );
};

export default SleepData;
